import {
  computeEstimatedRoaChf,
  computeExpectedRoaChf,
  STAGE_CHIP,
  STAGE_LABEL_FR,
  type Stage,
} from '@crm/shared';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import type { Custodian, Opportunity, OpportunityInlineEditPayload } from '../../lib/api';
import { CLIENT_NAME } from '../../lib/branding';
import { formatBps, formatChf } from '../../lib/format';

import { PipelineColumnFilter, type ColumnFilterState } from './PipelineColumnFilter';
import { useInlineEdit } from './usePipelineInlineEdit';

type Props = {
  opportunities: Opportunity[];
  custodians: Custodian[];
  onPatched: (opportunity: Opportunity) => void;
  /**
   * When true: no inline editing, no link on the name column. Used by the
   * manager view where rows are anonymized and managers cannot drill into
   * PII via /opportunities/:id.
   */
  readOnly?: boolean;
};

/**
 * Pipeline view #3 — "Tableau" mode. Strict reproduction of the
 * Pipeline_CJE_22_02_2026.xlsx layout: 14 columns in the exact source order,
 * dark navy header, zebra body, sticky first column / header / totals.
 *
 * Layout contract (do NOT renumber without updating the Excel export):
 *   A Opportunity · B Passport/Origin · C Domiciled Country · D Industry
 *   E Total Wealth (Mio) · F Initial Expected · G RoA (bps)
 *   H Estimated RoA (computed, read-only)
 *   I Potential Expected Y+1 · J Timeframe
 *   K Expected RoA (computed, read-only)
 *   L Existing Bank · M Target Bank · N Status
 */
type SortDir = 'asc' | 'desc' | null;
type ColumnKey =
  | 'name'
  | 'passport_origin'
  | 'domiciled_country'
  | 'industry_sector'
  | 'total_wealth'
  | 'initial_expected_amount'
  | 'roa_bps'
  | 'estimated_roa_chf'
  | 'potential_expected_amount_y1'
  | 'expected_timeframe'
  | 'expected_roa_chf'
  | 'existing_bank'
  | 'target_bank'
  | 'stage';

type ColumnDef = {
  key: ColumnKey;
  // Excel header label — capitalization is part of the spec.
  label: string;
  align: 'left' | 'right';
  /** Width hint in px — used for column widths + horizontal layout stability. */
  width: number;
  numeric?: boolean;
  computed?: boolean;
  filter: 'text' | 'multi' | 'range' | 'multi-status' | 'none';
};

const COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'OPPORTUNITY', align: 'left', width: 240, filter: 'text' },
  {
    key: 'passport_origin',
    label: 'PASSPORT / ORIGIN',
    align: 'left',
    width: 140,
    filter: 'multi',
  },
  {
    key: 'domiciled_country',
    label: 'DOMICILED COUNTRY',
    align: 'left',
    width: 160,
    filter: 'multi',
  },
  {
    key: 'industry_sector',
    label: 'INDUSTRY / SECTOR',
    align: 'left',
    width: 180,
    filter: 'multi',
  },
  {
    key: 'total_wealth',
    label: 'TOTAL WEALTH (Mio)',
    align: 'right',
    width: 140,
    numeric: true,
    filter: 'range',
  },
  {
    key: 'initial_expected_amount',
    label: 'Initial Expected Amount - As of TODAY',
    align: 'right',
    width: 180,
    numeric: true,
    filter: 'range',
  },
  {
    key: 'roa_bps',
    label: 'RoA (bps)',
    align: 'right',
    width: 100,
    numeric: true,
    filter: 'range',
  },
  {
    key: 'estimated_roa_chf',
    label: 'Estimated RoA',
    align: 'right',
    width: 140,
    numeric: true,
    computed: true,
    filter: 'range',
  },
  {
    key: 'potential_expected_amount_y1',
    label: 'Potential expected Amount Year 2026',
    align: 'right',
    width: 200,
    numeric: true,
    filter: 'range',
  },
  {
    key: 'expected_timeframe',
    label: 'Expected Timeframe',
    align: 'left',
    width: 140,
    filter: 'multi',
  },
  {
    key: 'expected_roa_chf',
    label: 'Expected RoA',
    align: 'right',
    width: 140,
    numeric: true,
    computed: true,
    filter: 'range',
  },
  { key: 'existing_bank', label: 'Existing BANK', align: 'left', width: 130, filter: 'multi' },
  { key: 'target_bank', label: 'Target BANK', align: 'left', width: 130, filter: 'multi' },
  { key: 'stage', label: 'STATUS', align: 'left', width: 180, filter: 'multi-status' },
];

const PIPELINE_FILTER_KEY = 'crm.pipeline.table.filters';
const PIPELINE_SORT_KEY = 'crm.pipeline.table.sort';

function readFilters(): Record<string, ColumnFilterState> {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.localStorage.getItem(PIPELINE_FILTER_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function readSort(): { key: ColumnKey | null; dir: SortDir } {
  if (typeof window === 'undefined') return { key: null, dir: null };
  try {
    const raw = window.localStorage.getItem(PIPELINE_SORT_KEY);
    if (!raw) return { key: null, dir: null };
    return JSON.parse(raw);
  } catch {
    return { key: null, dir: null };
  }
}

/**
 * Pull the value used for sorting + comparison. For column I (potential),
 * text markers ("Negociation") receive a sentinel so they sink to the bottom
 * in ASC mode and rise to the top in DESC mode (per spec).
 */
function sortValue(o: Opportunity, key: ColumnKey): string | number | null {
  switch (key) {
    case 'estimated_roa_chf':
      return o.estimated_roa_chf;
    case 'expected_roa_chf':
      return o.expected_roa_chf;
    case 'potential_expected_amount_y1':
      return o.potential_negotiation ? null : o.potential_expected_amount_y1;
    case 'target_bank':
      return o.target_bank?.code ?? '';
    case 'name':
    case 'passport_origin':
    case 'domiciled_country':
    case 'industry_sector':
    case 'expected_timeframe':
    case 'existing_bank':
    case 'stage':
      return (o[key] as string | null) ?? '';
    default:
      return (o[key] as number | null) ?? null;
  }
}

function compareForSort(
  a: Opportunity,
  b: Opportunity,
  key: ColumnKey,
  dir: 'asc' | 'desc',
): number {
  const av = sortValue(a, key);
  const bv = sortValue(b, key);
  const aNull = av == null;
  const bNull = bv == null;
  // Text markers and null values pinned to the bottom on ASC, top on DESC.
  if (aNull && bNull) return 0;
  if (aNull) return dir === 'asc' ? 1 : -1;
  if (bNull) return dir === 'asc' ? -1 : 1;
  if (av === bv) return 0;
  return (av > bv ? 1 : -1) * (dir === 'asc' ? 1 : -1);
}

export function PipelineTable({ opportunities, custodians, onPatched, readOnly }: Props) {
  const [sort, setSort] = useState<{ key: ColumnKey | null; dir: SortDir }>(() => readSort());
  const [filters, setFilters] = useState<Record<string, ColumnFilterState>>(() => readFilters());

  const distinctValues = useMemo(() => buildDistinctValues(opportunities), [opportunities]);

  const filtered = useMemo(() => {
    let rows = opportunities;
    for (const col of COLUMNS) {
      const f = filters[col.key];
      if (!f) continue;
      rows = rows.filter((o) => matchesFilter(o, col, f));
    }
    if (sort.key && sort.dir) {
      rows = [...rows].sort((a, b) => compareForSort(a, b, sort.key!, sort.dir!));
    }
    return rows;
  }, [opportunities, filters, sort]);

  const totals = useMemo(() => computeTotals(filtered), [filtered]);

  function cycleSort(key: ColumnKey) {
    setSort((curr) => {
      let next: { key: ColumnKey | null; dir: SortDir };
      if (curr.key !== key) {
        next = { key, dir: 'asc' };
      } else if (curr.dir === 'asc') {
        next = { key, dir: 'desc' };
      } else {
        next = { key: null, dir: null };
      }
      try {
        window.localStorage.setItem(PIPELINE_SORT_KEY, JSON.stringify(next));
      } catch {
        /* swallow */
      }
      return next;
    });
  }

  function setColumnFilter(key: string, state: ColumnFilterState | null) {
    setFilters((curr) => {
      const next = { ...curr };
      if (state == null || isEmptyFilter(state)) delete next[key];
      else next[key] = state;
      try {
        window.localStorage.setItem(PIPELINE_FILTER_KEY, JSON.stringify(next));
      } catch {
        /* swallow */
      }
      return next;
    });
  }

  const inline = useInlineEdit({ onPatched });
  const editingKey = inline.editing
    ? `${inline.editing.opportunityId}:${inline.editing.column}`
    : null;

  async function exportXlsx() {
    const ids = filtered.map((o) => o.id).join(',');
    const qs = ids ? `?ids=${encodeURIComponent(ids)}` : '';
    const res = await fetch(`/api/v1/opportunities/export-xlsx${qs}`, {
      credentials: 'include',
    });
    if (!res.ok) {
      // eslint-disable-next-line no-alert
      alert(`Export failed (HTTP ${res.status}).`);
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Pipeline_${CLIENT_NAME}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="px-8 pb-10">
      <div className="flex items-center justify-between mb-3 text-xs text-ink-secondary">
        <span>
          {filtered.length} / {opportunities.length} row{filtered.length > 1 ? 's' : ''}
          {Object.keys(filters).length > 0 && ' · filtered'}
        </span>
        <button
          type="button"
          onClick={exportXlsx}
          className="btn-secondary !min-h-0 !py-1.5 !text-[12px]"
        >
          Export Excel
        </button>
      </div>
      <div className="card overflow-hidden">
        <div className="overflow-auto max-h-[72vh] relative">
          <table
            className="border-collapse"
            style={{ minWidth: COLUMNS.reduce((s, c) => s + c.width, 0) }}
          >
            <colgroup>
              {COLUMNS.map((c) => (
                <col key={c.key} style={{ width: c.width }} />
              ))}
            </colgroup>
            <thead className="sticky top-0 z-20">
              <tr className="bg-graphite text-white" style={{ height: 48 }}>
                {COLUMNS.map((col, idx) => (
                  <th
                    key={col.key}
                    scope="col"
                    className={
                      'px-3 align-middle text-[11px] uppercase font-semibold whitespace-pre-line ' +
                      (col.align === 'right' ? 'text-right ' : 'text-left ') +
                      (idx === 0 ? 'sticky left-0 z-30 bg-graphite ' : '')
                    }
                    style={{ letterSpacing: '0.05em' }}
                  >
                    <button
                      type="button"
                      onClick={() => cycleSort(col.key)}
                      className={
                        'group inline-flex items-center gap-1.5 select-none ' +
                        (col.align === 'right' ? 'flex-row-reverse' : '')
                      }
                      aria-label={`Sort by ${col.label}`}
                    >
                      <span className="leading-tight">{col.label}</span>
                      <SortIndicator active={sort.key === col.key} dir={sort.dir} />
                    </button>
                    {col.filter !== 'none' && (
                      <PipelineColumnFilter
                        columnKey={col.key}
                        filterType={col.filter}
                        align={col.align}
                        value={filters[col.key] ?? null}
                        distinct={distinctValues[col.key] ?? []}
                        onChange={(s) => setColumnFilter(col.key, s)}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="bg-white">
              {filtered.map((o, idx) => (
                <PipelineRow
                  key={o.id}
                  opportunity={o}
                  zebra={idx % 2 === 1}
                  custodians={custodians}
                  editingKey={editingKey}
                  inline={inline}
                  readOnly={readOnly}
                />
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={COLUMNS.length}
                    className="px-4 py-12 text-center text-sm italic text-ink-tertiary"
                  >
                    No opportunities match the filters.
                  </td>
                </tr>
              )}
            </tbody>

            {filtered.length > 0 && (
              <tfoot className="sticky bottom-0 z-20">
                <tr style={{ background: '#E3E5E6', borderTop: '2px solid #1A2832' }}>
                  {COLUMNS.map((col, idx) => (
                    <td
                      key={col.key}
                      className={
                        'px-3 py-2 text-[12px] font-semibold text-graphite ' +
                        (col.align === 'right' ? 'text-right' : 'text-left') +
                        (idx === 0 ? ' sticky left-0 z-30' : '')
                      }
                      style={idx === 0 ? { background: '#E3E5E6' } : {}}
                    >
                      <TotalsCell colKey={col.key} totals={totals} />
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Row + cells
// -----------------------------------------------------------------------------
type InlineCtx = ReturnType<typeof useInlineEdit>;

function PipelineRow({
  opportunity: o,
  zebra,
  custodians,
  editingKey,
  inline,
  readOnly,
}: {
  opportunity: Opportunity;
  zebra: boolean;
  custodians: Custodian[];
  editingKey: string | null;
  inline: InlineCtx;
  readOnly?: boolean;
}) {
  const rowBg = zebra ? '#EEF4F8' : '#FFFFFF';
  const chip = STAGE_CHIP[o.stage as Stage];

  return (
    <tr className="group hover:[--row-bg:#EEF4F8] transition-colors" style={{ background: rowBg }}>
      {COLUMNS.map((col, idx) => {
        const sticky = idx === 0;
        const stickyStyle = sticky
          ? { position: 'sticky' as const, left: 0, background: rowBg, zIndex: 5 }
          : {};
        const editing = editingKey === `${o.id}:${col.key}` && !readOnly;
        const right = col.align === 'right';
        const cellClass =
          'px-3 align-middle text-[13px] text-ink ' +
          (right ? 'text-right' : 'text-left') +
          ' ' +
          (col.numeric ? 'font-tabular' : '');

        return (
          <td key={col.key} className={cellClass} style={{ height: 36, ...stickyStyle }}>
            {renderCell(col, o, custodians, inline, editing, chip, readOnly)}
          </td>
        );
      })}
    </tr>
  );
}

function renderCell(
  col: ColumnDef,
  o: Opportunity,
  custodians: Custodian[],
  inline: InlineCtx,
  isEditing: boolean,
  chip: { bg: string; fg: string },
  readOnly?: boolean,
) {
  // --- READ-ONLY cells -------------------------------------------------------
  if (col.key === 'name') {
    if (readOnly) {
      // Manager view: the value is already a pseudonym; no link to the
      // detail page (would expose PII).
      return (
        <span className="text-ink font-medium block truncate font-tabular" title={o.name}>
          {o.name}
        </span>
      );
    }
    return (
      <Link
        to={`/opportunities/${o.id}`}
        className="text-ink hover:text-graphite font-medium block truncate"
        title={o.name}
      >
        {o.name}
      </Link>
    );
  }

  if (col.key === 'estimated_roa_chf' || col.key === 'expected_roa_chf') {
    return <ComputedCell col={col.key} o={o} />;
  }

  if (col.key === 'stage') {
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full font-medium"
        style={{ background: chip.bg, color: chip.fg }}
      >
        {STAGE_LABEL_FR[o.stage as Stage]}
      </span>
    );
  }

  // --- Editable cells -------------------------------------------------------
  if (isEditing) {
    return <InlineEditor col={col} opportunity={o} custodians={custodians} inline={inline} />;
  }

  const editable =
    !readOnly &&
    (col.key === 'passport_origin' ||
      col.key === 'domiciled_country' ||
      col.key === 'industry_sector' ||
      col.key === 'total_wealth' ||
      col.key === 'initial_expected_amount' ||
      col.key === 'roa_bps' ||
      col.key === 'potential_expected_amount_y1' ||
      col.key === 'expected_timeframe' ||
      col.key === 'existing_bank' ||
      col.key === 'target_bank');

  const display = displayValue(col, o);

  const handleClick = editable
    ? () => inline.startEdit({ opportunityId: o.id, column: col.key })
    : undefined;

  if (col.key === 'existing_bank' || col.key === 'target_bank') {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="inline-flex items-center px-2 py-0.5 rounded-full bg-platinum/60 text-[11px] text-ink hover:bg-platinum"
        title="Cliquer pour modifier"
      >
        {display || <span className="text-ink-tertiary">—</span>}
      </button>
    );
  }

  if (col.key === 'potential_expected_amount_y1' && o.potential_negotiation) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="text-ink-secondary italic text-left w-full"
      >
        Negociation
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!editable}
      className={
        'w-full ' +
        (col.align === 'right' ? 'text-right' : 'text-left') +
        (editable ? ' hover:bg-platinum/50 rounded px-1 -mx-1' : '') +
        (display === '' ? ' text-ink-tertiary' : '')
      }
      title={editable ? 'Cliquer pour modifier' : undefined}
    >
      {display || <span className="text-ink-tertiary">—</span>}
    </button>
  );
}

function displayValue(col: ColumnDef, o: Opportunity): string {
  switch (col.key) {
    case 'passport_origin':
      return o.passport_origin ?? '';
    case 'domiciled_country':
      return o.domiciled_country;
    case 'industry_sector':
      return o.industry_sector;
    case 'total_wealth':
      // Excel labels this "TOTAL WEALTH (Mio)" but the stored value is in CHF.
      // Display in millions with 1 decimal to match the source workbook density.
      return o.total_wealth ? (o.total_wealth / 1_000_000).toFixed(1) : '';
    case 'initial_expected_amount':
      return formatChf(o.initial_expected_amount);
    case 'roa_bps':
      return formatBps(o.roa_bps);
    case 'potential_expected_amount_y1':
      return o.potential_negotiation ? 'Negociation' : formatChf(o.potential_expected_amount_y1);
    case 'expected_timeframe':
      return o.expected_timeframe ?? '';
    case 'existing_bank':
      return o.existing_bank ?? '';
    case 'target_bank':
      return o.target_bank?.code ?? '';
    default:
      return '';
  }
}

function ComputedCell({
  col,
  o,
}: {
  col: 'estimated_roa_chf' | 'expected_roa_chf';
  o: Opportunity;
}) {
  const value =
    col === 'estimated_roa_chf'
      ? computeEstimatedRoaChf(o.initial_expected_amount, o.roa_bps)
      : computeExpectedRoaChf(
          o.potential_negotiation ? null : o.potential_expected_amount_y1,
          o.roa_bps,
        );
  if (value == null) {
    return <span className="text-ink-tertiary cursor-not-allowed">—</span>;
  }
  const tooltip =
    col === 'estimated_roa_chf'
      ? `= ${formatThousands(o.initial_expected_amount)} × (${o.roa_bps} / 10'000) = ${formatThousands(
          value,
        )}`
      : `= ${formatThousands(o.potential_expected_amount_y1)} × (${o.roa_bps} / 10'000) = ${formatThousands(
          value,
        )}`;
  return (
    <span
      title={tooltip}
      className="cursor-help underline decoration-dotted decoration-ink-tertiary/40 underline-offset-2"
    >
      {formatChf(value)}
    </span>
  );
}

function formatThousands(n: number): string {
  return new Intl.NumberFormat('fr-CH').format(Math.round(n));
}

// -----------------------------------------------------------------------------
// Inline editor (input/select rendered in-cell while editing)
// -----------------------------------------------------------------------------
function InlineEditor({
  col,
  opportunity,
  custodians,
  inline,
}: {
  col: ColumnDef;
  opportunity: Opportunity;
  custodians: Custodian[];
  inline: InlineCtx;
}) {
  const commit = (patch: OpportunityInlineEditPayload) => {
    void inline.commit(opportunity.id, patch);
  };

  switch (col.key) {
    case 'total_wealth': {
      return (
        <NumberInput
          autoFocus
          value={opportunity.total_wealth / 1_000_000}
          step={0.1}
          onCommit={(n) => commit({ total_wealth: Math.round((n ?? 0) * 1_000_000) })}
        />
      );
    }
    case 'initial_expected_amount': {
      return (
        <NumberInput
          autoFocus
          value={opportunity.initial_expected_amount}
          step={1_000}
          onCommit={(n) => commit({ initial_expected_amount: n ?? 0 })}
        />
      );
    }
    case 'roa_bps': {
      return (
        <NumberInput
          autoFocus
          value={opportunity.roa_bps}
          step={1}
          onCommit={(n) => commit({ roa_bps: n })}
        />
      );
    }
    case 'potential_expected_amount_y1': {
      return <PotentialEditor opportunity={opportunity} onCommit={(patch) => commit(patch)} />;
    }
    case 'expected_timeframe': {
      return (
        <TextInput
          autoFocus
          value={opportunity.expected_timeframe ?? ''}
          placeholder="ex: 3 months"
          onCommit={(v) => commit({ expected_timeframe: v.trim() || null })}
        />
      );
    }
    case 'existing_bank': {
      return (
        <TextInput
          autoFocus
          value={opportunity.existing_bank ?? ''}
          onCommit={(v) => commit({ existing_bank: v.trim() || null })}
        />
      );
    }
    case 'target_bank': {
      return (
        <SelectInput
          autoFocus
          value={opportunity.target_bank?.id ?? ''}
          options={[
            { value: '', label: '—' },
            ...custodians.map((c) => ({ value: c.id, label: c.code })),
          ]}
          onCommit={(v) => commit({ target_bank_id: v || null })}
        />
      );
    }
    case 'passport_origin': {
      return (
        <TextInput
          autoFocus
          value={opportunity.passport_origin ?? ''}
          onCommit={(v) => commit({ passport_origin: v.trim() || null })}
        />
      );
    }
    case 'domiciled_country': {
      return (
        <TextInput
          autoFocus
          value={opportunity.domiciled_country}
          onCommit={(v) => v.trim() && commit({ domiciled_country: v.trim() })}
        />
      );
    }
    case 'industry_sector': {
      return (
        <TextInput
          autoFocus
          value={opportunity.industry_sector}
          onCommit={(v) => v.trim() && commit({ industry_sector: v.trim() })}
        />
      );
    }
    default:
      return null;
  }
}

function PotentialEditor({
  opportunity,
  onCommit,
}: {
  opportunity: Opportunity;
  onCommit: (patch: OpportunityInlineEditPayload) => void;
}) {
  const [mode, setMode] = useState<'amount' | 'negotiation'>(
    opportunity.potential_negotiation ? 'negotiation' : 'amount',
  );
  return (
    <div className="inline-flex items-center gap-1 w-full">
      <select
        value={mode}
        onChange={(e) => setMode(e.target.value as 'amount' | 'negotiation')}
        className="text-[11px] border border-platinum bg-white rounded px-1 py-0.5"
      >
        <option value="amount">Montant</option>
        <option value="negotiation">Negociation</option>
      </select>
      {mode === 'amount' ? (
        <NumberInput
          value={
            opportunity.potential_negotiation ? null : opportunity.potential_expected_amount_y1
          }
          step={1_000}
          onCommit={(n) =>
            onCommit({
              potential_expected_amount_y1: n ?? 0,
              potential_negotiation: false,
            })
          }
        />
      ) : (
        <button
          type="button"
          onClick={() =>
            onCommit({
              potential_expected_amount_y1: 0,
              potential_negotiation: true,
            })
          }
          className="text-[12px] italic text-ink-secondary px-1"
        >
          Confirmer
        </button>
      )}
    </div>
  );
}

function NumberInput({
  value,
  step,
  autoFocus,
  onCommit,
}: {
  value: number | null;
  step?: number;
  autoFocus?: boolean;
  onCommit: (n: number | null) => void;
}) {
  const [v, setV] = useState(value == null ? '' : String(value));
  return (
    <input
      autoFocus={autoFocus}
      type="number"
      step={step}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => onCommit(v === '' ? null : Number(v))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setV(value == null ? '' : String(value));
          (e.target as HTMLInputElement).blur();
        }
      }}
      className="w-full bg-white border border-graphite/40 rounded px-1 py-0.5 text-[13px] font-tabular text-right outline-none focus:border-graphite"
    />
  );
}

function TextInput({
  value,
  autoFocus,
  placeholder,
  onCommit,
}: {
  value: string;
  autoFocus?: boolean;
  placeholder?: string;
  onCommit: (v: string) => void;
}) {
  const [v, setV] = useState(value);
  return (
    <input
      autoFocus={autoFocus}
      type="text"
      value={v}
      placeholder={placeholder}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => onCommit(v)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setV(value);
          (e.target as HTMLInputElement).blur();
        }
      }}
      className="w-full bg-white border border-graphite/40 rounded px-1 py-0.5 text-[13px] outline-none focus:border-graphite"
    />
  );
}

function SelectInput({
  value,
  options,
  autoFocus,
  onCommit,
}: {
  value: string;
  options: { value: string; label: string }[];
  autoFocus?: boolean;
  onCommit: (v: string) => void;
}) {
  return (
    <select
      autoFocus={autoFocus}
      value={value}
      onChange={(e) => onCommit(e.target.value)}
      onBlur={(e) => onCommit(e.target.value)}
      className="w-full bg-white border border-graphite/40 rounded px-1 py-0.5 text-[13px] outline-none focus:border-graphite"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function SortIndicator({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active || dir == null)
    return (
      <span aria-hidden="true" className="opacity-30 text-[10px]">
        ↕
      </span>
    );
  return (
    <span aria-hidden="true" className="text-[10px]">
      {dir === 'asc' ? '↑' : '↓'}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Totals
// -----------------------------------------------------------------------------
export type PipelineTotals = {
  count: number;
  total_wealth: number;
  initial_expected_amount: number;
  roa_bps_avg: number | null;
  estimated_roa_chf: number;
  potential_expected_amount_y1: number;
  expected_roa_chf: number;
};

export function computeTotals(rows: Opportunity[]): PipelineTotals {
  let twSum = 0;
  let initSum = 0;
  let potSum = 0;
  let estSum = 0;
  let expSum = 0;
  let bpsSum = 0;
  let bpsCount = 0;
  for (const r of rows) {
    twSum += r.total_wealth ?? 0;
    initSum += r.initial_expected_amount ?? 0;
    if (r.roa_bps != null) {
      bpsSum += r.roa_bps;
      bpsCount += 1;
    }
    if (!r.potential_negotiation) {
      potSum += r.potential_expected_amount_y1 ?? 0;
    }
    const est = computeEstimatedRoaChf(r.initial_expected_amount, r.roa_bps);
    if (est != null) estSum += est;
    const exp = computeExpectedRoaChf(
      r.potential_negotiation ? null : r.potential_expected_amount_y1,
      r.roa_bps,
    );
    if (exp != null) expSum += exp;
  }
  return {
    count: rows.length,
    total_wealth: twSum,
    initial_expected_amount: initSum,
    roa_bps_avg: bpsCount === 0 ? null : bpsSum / bpsCount,
    estimated_roa_chf: estSum,
    potential_expected_amount_y1: potSum,
    expected_roa_chf: expSum,
  };
}

function TotalsCell({ colKey, totals }: { colKey: ColumnKey; totals: PipelineTotals }) {
  switch (colKey) {
    case 'name':
      return <span>TOTAL · {totals.count} opp</span>;
    case 'total_wealth':
      return <span>{(totals.total_wealth / 1_000_000).toFixed(1)}</span>;
    case 'initial_expected_amount':
      return <span>{formatChf(totals.initial_expected_amount)}</span>;
    case 'roa_bps':
      return <span className="text-ink-secondary">Ø {formatBps(totals.roa_bps_avg)}</span>;
    case 'estimated_roa_chf':
      return <span>{formatChf(totals.estimated_roa_chf)}</span>;
    case 'potential_expected_amount_y1':
      return <span>{formatChf(totals.potential_expected_amount_y1)}</span>;
    case 'expected_roa_chf':
      return <span>{formatChf(totals.expected_roa_chf)}</span>;
    default:
      return null;
  }
}

// -----------------------------------------------------------------------------
// Filter machinery
// -----------------------------------------------------------------------------
function buildDistinctValues(rows: Opportunity[]): Record<string, string[]> {
  const keys = [
    'passport_origin',
    'domiciled_country',
    'industry_sector',
    'expected_timeframe',
    'existing_bank',
    'target_bank',
    'stage',
  ] as const;
  const acc: Record<string, Set<string>> = Object.fromEntries(
    keys.map((k) => [k, new Set<string>()]),
  );
  for (const o of rows) {
    if (o.passport_origin) acc.passport_origin!.add(o.passport_origin);
    acc.domiciled_country!.add(o.domiciled_country);
    acc.industry_sector!.add(o.industry_sector);
    if (o.expected_timeframe) acc.expected_timeframe!.add(o.expected_timeframe);
    if (o.existing_bank) acc.existing_bank!.add(o.existing_bank);
    if (o.target_bank?.code) acc.target_bank!.add(o.target_bank.code);
    acc.stage!.add(o.stage);
  }
  const out: Record<string, string[]> = {};
  for (const k of keys) out[k] = Array.from(acc[k]!).sort();
  return out;
}

function matchesFilter(o: Opportunity, col: ColumnDef, f: ColumnFilterState): boolean {
  if (f.type === 'text' && f.query) {
    const haystack = String(o[col.key as keyof Opportunity] ?? '').toLowerCase();
    return haystack.includes(f.query.toLowerCase());
  }
  if (f.type === 'multi' && f.selected && f.selected.length > 0) {
    if (col.key === 'target_bank') return f.selected.includes(o.target_bank?.code ?? '');
    return f.selected.includes(String(o[col.key as keyof Opportunity] ?? ''));
  }
  if (f.type === 'multi-status' && f.selected && f.selected.length > 0) {
    return f.selected.includes(o.stage);
  }
  if (f.type === 'range') {
    let v: number | null = null;
    if (col.key === 'estimated_roa_chf') v = o.estimated_roa_chf;
    else if (col.key === 'expected_roa_chf') v = o.expected_roa_chf;
    else if (col.key === 'potential_expected_amount_y1')
      v = o.potential_negotiation ? null : o.potential_expected_amount_y1;
    else v = (o[col.key as keyof Opportunity] as number | null) ?? null;
    if (v == null) return false;
    if (f.min != null && v < f.min) return false;
    if (f.max != null && v > f.max) return false;
  }
  return true;
}

function isEmptyFilter(state: ColumnFilterState): boolean {
  if (state.type === 'text') return !state.query;
  if (state.type === 'multi' || state.type === 'multi-status')
    return !state.selected || state.selected.length === 0;
  if (state.type === 'range') return state.min == null && state.max == null;
  return true;
}
